<template>
    <div class="initiator-picker">
        <div v-if="searchable && tiles.length > searchThreshold" class="row justify-content-center mb-4">
            <div class="col-md-6 col-lg-5">
                <input
                    v-model="search"
                    type="search"
                    class="form-control"
                    :placeholder="searchPlaceholder"
                />
                <div v-if="search" class="form-text text-center">
                    {{ filteredTiles.length }} of {{ tiles.length }} match
                </div>
            </div>
        </div>

        <div v-if="filteredTiles.length" class="initiator-grid">
            <div
                v-for="tile in filteredTiles"
                :key="tile.value"
                class="card initiator-tile"
                :class="[currentTile == tile.value ? 'active' : '']"
                @click="tileClick(tile.value)"
            >
                <span v-show="currentTile == tile.value" class="badge"><i class="fa fa-check"></i></span>
                <div class="card-body">
                    <div class="initiator-logo">
                        <img
                            v-if="logoSrc(tile) && !brokenLogos.includes(tile.value)"
                            :src="logoSrc(tile)"
                            :alt="tile.title"
                            loading="lazy"
                            @error="onLogoError(tile)"
                        />
                        <span v-else class="initiator-logo-fallback">{{ initials(tile.title) }}</span>
                    </div>
                    <h4 class="card-title text-center mb-1">{{ tile.title }}</h4>
                    <h5
                        v-if="tile.subTitle"
                        class="card-subtitle text-center text-muted initiator-desc"
                        :title="tile.subTitle"
                    >
                        {{ tile.subTitle }}
                    </h5>
                </div>
            </div>
        </div>
        <div v-else class="text-center text-muted py-6">
            No initiators match "{{ search }}".
        </div>
    </div>
</template>

<script>
export default {
    name: "InitiatorPicker",
    props: {
        // Tiles shaped as { title, subTitle, value, iconSrc } - same contract as Tiles.vue
        tiles: {
            type: Array,
            default: () => []
        },
        selectedTile: [String, Boolean],
        searchable: {
            type: Boolean,
            default: true
        },
        searchPlaceholder: {
            type: String,
            default: "Search initiators..."
        },
        searchThreshold: {
            type: Number,
            default: 6
        }
    },
    data() {
        return {
            currentTile: this.selectedTile,
            search: "",
            brokenLogos: []
        }
    },
    watch: {
        selectedTile(val) {
            this.currentTile = val;
        }
    },
    computed: {
        filteredTiles() {
            if (!this.search) return this.tiles;
            const term = this.search.toLowerCase();
            return this.tiles.filter((tile) => {
                return `${tile.title || ""} ${tile.subTitle || ""}`.toLowerCase().includes(term);
            });
        }
    },
    methods: {
        tileClick(val) {
            this.currentTile = this.currentTile == val ? null : val;
            this.$emit('onSelectTile', this.currentTile);
        },
        logoSrc(tile) {
            const src = tile.iconSrc;
            if (!src) return null;
            if (src.includes('base64')) return src;
            try {
                return require("../../assets/" + src);
            } catch (e) {
                return null;
            }
        },
        onLogoError(tile) {
            if (!this.brokenLogos.includes(tile.value)) this.brokenLogos.push(tile.value);
        },
        initials(title) {
            return (title || "")
                .split(/[\s\-&/]+/)
                .filter((word) => /^[a-z]/i.test(word))
                .slice(0, 3)
                .map((word) => word[0].toUpperCase())
                .join("");
        }
    },
    emits: ['onSelectTile']
}
</script>

<style scoped>
    .initiator-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(8rem, 9rem));
        justify-content: center;
        gap: 0.75rem;
        max-width: 76rem;
        margin: 0 auto 1.5rem;
    }

    .initiator-tile {
        position: relative;
        margin-bottom: 0;
        cursor: pointer;
    }

    .initiator-tile:hover,
    .initiator-tile.active {
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
        background-color: #2C7BE5;
        background-image: radial-gradient(#2e77dc, #2C7BE5);
        color: #ffff;
    }

    .initiator-tile .card-body {
        padding: 0.875rem 0.75rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .initiator-tile .card-title {
        font-size: 0.8125rem;
        line-height: 1.3;
    }

    .initiator-tile:hover .text-muted,
    .initiator-tile.active .text-muted {
        color: #d3e2f7 !important;
    }

    .initiator-logo {
        height: 2.75rem;
        margin-bottom: 0.625rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #ffffff;
        border: 1px solid rgba(18, 38, 63, 0.06);
        border-radius: 0.375rem;
        padding: 0.25rem 0.5rem;
        overflow: hidden;
    }

    .initiator-logo img {
        width: 100%;
        height: 2rem;
        object-fit: contain;
    }

    .initiator-logo-fallback {
        font-weight: 600;
        font-size: 1rem;
        color: #95AAC9;
    }

    .initiator-desc {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 0;
        font-size: 0.6875rem;
        line-height: 1.3;
    }

    .badge {
        position: absolute;
        color: #003B67;
        background-color: #ffffff;
        box-shadow: inset 0px 0px 6px 6px #E0E0E0;
        font-weight: bold;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        padding: 5px;
        font-size: 16px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1.5px solid #ffffff;
        top: -8px;
        left: -8px;
        z-index: 2;
    }
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .initiator-picker {
        .initiator-tile .badge {
            background-color: $dark-input-bg !important;
            box-shadow: inset 0px 0px 6px 6px $dark-body-bg !important;
            color: #7ab0ff !important;
            border-color: $dark-border-color !important;
        }

        .initiator-tile:hover .text-muted,
        .initiator-tile.active .text-muted {
            color: #bad4ff !important;
        }

        .initiator-logo-fallback {
            color: $dark-text-secondary;
        }
    }
}
</style>
